/**
 * Non-energy charge defaults — single source of truth for the 3 canonical fields.
 *
 * Business rule (client-specified):
 *   • Meter Agreement       — one static value for EVERY retailer (the SHIELD/Powermetric
 *                             annual fee, $3,996). Never retailer-specific.
 *   • Supplementary Metering — retailer-specific.
 *   • Other Service Charge   — retailer-specific.
 *
 * Retailers not listed here (Aurora, Pacific Blue, Iberdrola, …) intentionally have NO
 * defaults: their charges are only ever whatever the offer document/email actually states.
 *
 * Resolution order at parse time is: value stated in the offer wins → otherwise blank, with
 * a wand button in the review UI that pulls the sys_config default on demand. These values
 * are NEVER silently substituted for a figure the retailer actually quoted.
 */

export type RetailerNonEnergyDefaults = {
    /** $/meter/year — retailer's own supplementary metering fee */
    supplementaryMetering?: number;
    /** $/year — retailer's service/admin fee, annualised */
    otherServiceCharge?: number;
};

export type NonEnergyDefaultsConfig = {
    /** Static SHIELD/Powermetric fee applied to every retailer */
    meterAgreement: number;
    /** Keyed by canonical retailer key (see RETAILER_ALIASES) */
    retailers: Record<string, RetailerNonEnergyDefaults>;
};

/**
 * Canonical retailer keys → the name variants seen in the `retailer` table and in parsed
 * documents. Matching is on the NORMALIZED name (lowercased, non-alphanumerics stripped)
 * and is EXACT, never substring — "ActewAGL" normalizes to "actewagl" which contains "agl",
 * so a substring match would silently resolve ActewAGL offers to AGL's defaults.
 */
export const RETAILER_ALIASES: Record<string, string[]> = {
    origin: ['origin', 'originenergy'],
    energyaustralia: ['energyaustralia', 'ea'],
    momentum: ['momentum', 'momentumenergy'],
    // 'aglenergy' is how the retailer is actually stored ("AGL Energy" — 369 offers on prod,
    // the largest single retailer). Listing only 'agl' meant resolveRetailerKey returned null
    // for every one of them, so the non-energy wand found no defaults and sat disabled, and
    // Service Charge $607 / AEMO Participant $175 were never seeded — the same $782 that went
    // missing from AGL's Other Charges. Safe alongside actewagl: matching is exact, so
    // 'actewagl' still resolves to its own key and never to this one.
    agl: ['agl', 'aglenergy'],
    actewagl: ['actewagl'],
    // Tango Energy rebranded to Flo — same charge schedule, so both names share one key.
    flo: ['flo', 'floenergy', 'floenergyaustralia', 'tango', 'tangoenergy'],
    engie: ['engie', 'engieenergy'],
    nextbusiness: ['nextbusinessenergy', 'nextbusiness', 'nbe', 'nextenergy'],
    smartest: ['smartestenergy', 'smartest'],
    nectr: ['nectr'],
    shell: ['shell', 'shellenergy'],
    // Job 69: both are seeded in `retailer_tou_rates` with real Peak/Shoulder
    // windows ('Aurora' for TAS, 'Zen Energy') and neither was declared here, so
    // resolveRetailerKey returned null and bv_process_retail_tou could not match
    // the contract's own spelling — "Aurora Energy" normalises to 'auroraenergy'
    // and the seeded row to 'aurora'. No band matched, and the band expression
    // ends in COALESCE(..., 'offpeak'), so every half-hour slot on those sites
    // landed in off-peak.
    //
    // These keys have no non-energy defaults configured, which is fine:
    // resolveNonEnergyDefaults returns {} for a key the config has no entry for,
    // so the tender side is unaffected by declaring them.
    aurora: ['aurora', 'auroraenergy'],
    zen: ['zen', 'zenenergy'],
};

/** Normalized alias → canonical key, built once from RETAILER_ALIASES. */
const ALIAS_TO_KEY: Record<string, string> = Object.entries(RETAILER_ALIASES)
    .reduce((acc, [key, aliases]) => {
        aliases.forEach(a => { acc[a] = key; });
        return acc;
    }, {} as Record<string, string>);

/** Lowercase and strip everything that isn't a letter or digit. */
const normalizeRetailerName = (name: string): string =>
    (name || '').toLowerCase().replace(/[^a-z0-9]/g, '');

/**
 * Resolve a raw retailer name (as stored in the `retailer` table) to its canonical key.
 * Returns null for retailers with no configured defaults — the caller must then leave the
 * fields blank rather than guessing.
 */
export const resolveRetailerKey = (name: string | null | undefined): string | null => {
    if (!name) return null;
    return ALIAS_TO_KEY[normalizeRetailerName(name)] || null;
};

/**
 * Look up a retailer's configured SM/OSC defaults. Returns an empty object (not null) when
 * the retailer is unknown or has no configured values, so callers can destructure safely.
 */
export const resolveNonEnergyDefaults = (
    config: NonEnergyDefaultsConfig | null | undefined,
    retailerName: string | null | undefined
): RetailerNonEnergyDefaults => {
    const key = resolveRetailerKey(retailerName);
    if (!key || !config?.retailers) return {};
    return config.retailers[key] || {};
};

/**
 * Keys whose values are a retailer's OWN metering-tier fee — the alternative to our static
 * $3,996 Meter Agreement, not an additional service charge.
 *
 * These must never be folded into Other Service Charge: every offer already gets the $3,996
 * SHIELD fee added by the calculation engine, so folding a retailer's $870–$1,320 metering
 * tier into OSC on top of it double-counts metering on every single offer.
 *
 * Matched against the normalized key (lowercased, underscores → spaces).
 */
const METERING_TIER_PATTERNS = [
    'meter agreement',
    'metering agreement',
    'meter charge',
    'metering charge',
    'metering standard',
    'metering annual',
    'metering no',            // metering_no_mc, metering_no_dma
    'metering ea',            // metering_ea_appointed — EA's own preferred MC
    'metering flo',           // metering_flo_appointed — Flo's own preferred MC
    'metering comms',         // metering_comms34, metering_commsc_d_mrim
];

/**
 * Supplementary Metering candidates, in PRIORITY order (most specific first).
 *
 * Retailers express the same "surcharge when the customer has their own DMA/metering
 * coordinator" fee under several names, and a single document often contains more than one
 * of them at different meanings — Momentum quotes BOTH dma_admin_fee ($185, the surcharge
 * alone) and metering_with_dma ($1,284, the surcharge PLUS its base metering charge). Summing
 * them, or taking whichever happened to be iterated first, produces a wrong figure; the
 * highest-priority match present is the one that means "supplementary fee on its own".
 *
 * These are deliberately checked BEFORE METERING_TIER_PATTERNS, which is what makes the
 * customer-appointed/DMA tiers land in Supplementary Metering (Origin $300, EA $95, AGL $175,
 * Flo $300) rather than being excluded as a retailer metering tier.
 */
const SUPPLEMENTARY_PATTERNS: string[] = [
    'supplementary',        // explicit — always wins
    'dma admin',            // Momentum dma_admin_fee: the surcharge alone
    'admin fee',
    'metering customer',    // EA/Flo metering_customer_appointed
    'metering dma',         // AGL metering_dma
    'dma surcharge',        // Pacific Blue
    'metering with',        // Origin metering_with_dma — last resort: may be a combined total
];

/**
 * Normalize a charge key to lowercase space-separated words, so the same field is classified
 * identically however it is spelled.
 *
 * ⚠️ The camelCase split is load-bearing, not cosmetic: parsed documents use snake_case
 * ("meter_agreement") but the offer row stores camelCase ("meterAgreement"). Splitting only on
 * underscores leaves "meteragreement", which matches none of the multi-word patterns below —
 * so a stored meterAgreement silently fell through to the catch-all and got summed into the
 * cost total, double-counting against the static Meter Agreement fee the calculation engine
 * already adds from sys_config.
 */
export const normalizeChargeKey = (key: string): string =>
    (key || '')
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')  // camelCase → "camel Case"
        .replace(/_/g, ' ')
        .toLowerCase()
        .replace(/\s+/g, ' ')
        .trim();

/**
 * True when a parsed non-energy key represents a retailer metering tier (see above) and so
 * must be excluded from the Other Service Charge fold and from any cost total.
 */
export const isMeteringTierKey = (key: string): boolean => {
    const k = normalizeChargeKey(key);
    return METERING_TIER_PATTERNS.some(p => k.includes(p));
};

/**
 * Keys that carry a Supplementary Metering value. Must be checked BEFORE isMeteringTierKey —
 * several of these ("metering customer appointed", "metering with dma") also match the
 * metering-tier patterns and would otherwise be excluded from the field entirely.
 */
export const isSupplementaryKey = (key: string): boolean => {
    const k = normalizeChargeKey(key);
    return SUPPLEMENTARY_PATTERNS.some(p => k.includes(p));
};

/**
 * Priority rank of a Supplementary Metering candidate — lower wins. Returns
 * Number.MAX_SAFE_INTEGER for keys that aren't supplementary candidates at all, so callers
 * can compare unconditionally. See SUPPLEMENTARY_PATTERNS for why priority matters.
 */
export const supplementaryPriority = (key: string): number => {
    const k = normalizeChargeKey(key);
    const idx = SUPPLEMENTARY_PATTERNS.findIndex(p => k.includes(p));
    return idx === -1 ? Number.MAX_SAFE_INTEGER : idx;
};

/**
 * Keys that carry a service/retail-service fee → Other Service Charge.
 */
export const isServiceChargeKey = (key: string): boolean => {
    const k = normalizeChargeKey(key);
    return k.includes('service') || k.includes('supply charge');
};

/**
 * Non-monetary fields some prompts emit alongside real charges (e.g. NBE's load_flex_percent,
 * a volume-variation band in %). Folding these into a dollar total would be nonsense.
 */
export const isNonMonetaryKey = (key: string): boolean => {
    const k = normalizeChargeKey(key);
    return k.includes('percent') || k.includes('pct') || k.endsWith(' band');
};

/** Fallback used when sys_config has no `non_energy_defaults` row at all. */
export const NON_ENERGY_DEFAULTS_FALLBACK: NonEnergyDefaultsConfig = {
    meterAgreement: 3996,
    retailers: {
        origin: { supplementaryMetering: 300, otherServiceCharge: 480 },
        energyaustralia: { supplementaryMetering: 95, otherServiceCharge: 650 },
        momentum: { supplementaryMetering: 185, otherServiceCharge: 550 },
        agl: { supplementaryMetering: 175, otherServiceCharge: 607 },
        flo: { supplementaryMetering: 300, otherServiceCharge: 365 },
        engie: { otherServiceCharge: 722.70 },
        nextbusiness: { supplementaryMetering: 360, otherServiceCharge: 480 },
        smartest: { otherServiceCharge: 550 },
        nectr: { supplementaryMetering: 500 },
        actewagl: { otherServiceCharge: 200 },
        shell: {},
    },
};
